import type { CSSProperties, ReactNode } from 'react';

/**
 * The app's skeleton primitives.
 *
 * Built on what the project already has rather than a new dependency: the
 * `shimmer` keyframe in tailwind.config.js, the `--cb-*` colour and radius
 * tokens, and Tailwind for layout. So a skeleton inherits the brand's surfaces
 * and corner radii and follows a re-theme without being touched.
 *
 * ACCESSIBILITY. The shapes are decorative — a screen reader should hear "this
 * region is loading", not a dozen empty boxes — so every shape is
 * `aria-hidden` and the region wrapper carries `aria-busy`. Use `SkeletonRegion`
 * (or put `aria-busy` on your own wrapper) around a block of them.
 *
 * MOTION. The shimmer is one transform-only sweep, which the compositor can run
 * without repainting, and it is switched off under `prefers-reduced-motion` —
 * see `.cb-skel` in globals.css.
 */

type Size = number | string;

const px = (v: Size | undefined) => (typeof v === 'number' ? `${v}px` : v);

export interface SkeletonProps {
  /** Any CSS width. A number is read as px. Defaults to filling the parent. */
  width?: Size;
  /** Any CSS height. A number is read as px. */
  height?: Size;
  /** Corner radius; defaults to the design system's small radius. */
  radius?: Size;
  /** Perfect circle — `width` is used for both axes. */
  circle?: boolean;
  className?: string;
  style?: CSSProperties;
}

/** The one shape everything else is built from. */
export const Skeleton = ({
  width, height, radius, circle = false, className = '', style,
}: SkeletonProps) => (
  <span
    aria-hidden="true"
    className={`cb-skel ${className}`}
    style={{
      width: px(width) ?? '100%',
      height: circle ? px(width) : px(height) ?? '1em',
      borderRadius: circle ? '9999px' : px(radius) ?? 'var(--cb-radius-s, .5rem)',
      ...style,
    }}
  />
);

/**
 * Wraps a block of shapes so assistive tech announces one busy region instead
 * of reading past a pile of hidden boxes.
 */
export const SkeletonRegion = ({
  label = 'Loading', className = '', children,
}: {
  label?: string;
  className?: string;
  children: ReactNode;
}) => (
  <div role="status" aria-busy="true" aria-label={label} className={className}>
    {children}
  </div>
);

/**
 * A paragraph of lines.
 *
 * The last line is short on purpose — a stack of equal bars reads as a table,
 * not as text, which is the whole point of matching the real layout.
 */
export const SkeletonText = ({
  lines = 3, height = 12, gap = 8, lastWidth = '60%', className = '',
}: {
  lines?: number;
  height?: Size;
  gap?: number;
  lastWidth?: Size;
  className?: string;
}) => (
  <span className={`flex flex-col ${className}`} style={{ gap }}>
    {Array.from({ length: lines }, (_, i) => (
      <Skeleton
        key={i}
        height={height}
        width={i === lines - 1 && lines > 1 ? lastWidth : '100%'}
        radius={999}
      />
    ))}
  </span>
);

export const SkeletonCircle = ({ size = 40, className = '' }: { size?: Size; className?: string }) => (
  <Skeleton circle width={size} className={className} />
);

/** Avatar plus the name/subtitle beside it — the shape used in lists and rows. */
export const SkeletonAvatar = ({
  size = 40, lines = 2, className = '',
}: {
  size?: Size;
  lines?: number;
  className?: string;
}) => (
  <span className={`flex items-center gap-3 ${className}`}>
    <SkeletonCircle size={size} />
    <span className="min-w-0 flex-1">
      <SkeletonText lines={lines} height={10} gap={6} lastWidth="45%" />
    </span>
  </span>
);

/** Art placeholder. `ratio` keeps the box the shape the real image will be. */
export const SkeletonImage = ({
  ratio = '16 / 9', height, radius, className = '',
}: {
  ratio?: string;
  height?: Size;
  radius?: Size;
  className?: string;
}) => (
  <Skeleton
    className={className}
    height={height}
    radius={radius ?? 'var(--cb-radius-m, .75rem)'}
    style={height ? undefined : { aspectRatio: ratio }}
  />
);

export const SkeletonButton = ({
  width = 110, height = 38, className = '',
}: {
  width?: Size;
  height?: Size;
  className?: string;
}) => <Skeleton width={width} height={height} className={className} />;

export const SkeletonBadge = ({ width = 64, className = '' }: { width?: Size; className?: string }) => (
  <Skeleton width={width} height={20} radius={999} className={className} />
);

/** Art over a caption — the shape of every game/product tile in this UI. */
export const SkeletonCard = ({
  imageHeight = 112, width, lines = 1, className = '',
}: {
  imageHeight?: Size;
  width?: Size;
  lines?: number;
  className?: string;
}) => (
  <span className={`flex shrink-0 flex-col gap-2 ${className}`} style={{ width: px(width) }}>
    <SkeletonImage height={imageHeight} radius="var(--cb-radius-s, .5rem)" />
    <SkeletonText lines={lines} height={11} gap={6} lastWidth="70%" />
  </span>
);

/**
 * One table row.
 *
 * `columns` takes the real column widths so the skeleton lines up with the
 * header above it — a row of equal bars under uneven headings is what makes a
 * loading table look broken rather than pending.
 */
export const SkeletonRow = ({
  columns = ['30%', '20%', '20%', '15%', '15%'], height = 12, className = '',
}: {
  columns?: Size[];
  height?: Size;
  className?: string;
}) => (
  <div className={`flex items-center gap-3 ${className}`}>
    {columns.map((w, i) => (
      <Skeleton key={i} width={w} height={height} radius={999} />
    ))}
  </div>
);

export const SkeletonTable = ({
  rows = 6, columns, header = true, className = '',
}: {
  rows?: number;
  columns?: Size[];
  header?: boolean;
  className?: string;
}) => (
  <SkeletonRegion label="Loading table" className={className}>
    {header && (
      <div className="border-b border-cb-line px-3 py-3">
        <SkeletonRow columns={columns} height={10} />
      </div>
    )}
    {Array.from({ length: rows }, (_, i) => (
      <div key={i} className="border-b border-cb-line px-3 py-3.5 last:border-b-0">
        <SkeletonRow columns={columns} />
      </div>
    ))}
  </SkeletonRegion>
);

/** A stack of avatar rows — the shape of every "recent activity" style list. */
export const SkeletonList = ({
  items = 5, avatar = true, className = '',
}: {
  items?: number;
  avatar?: boolean;
  className?: string;
}) => (
  <SkeletonRegion label="Loading list" className={`flex flex-col gap-3 ${className}`}>
    {Array.from({ length: items }, (_, i) =>
      avatar ? (
        <SkeletonAvatar key={i} />
      ) : (
        <SkeletonText key={i} lines={2} height={11} gap={6} />
      ),
    )}
  </SkeletonRegion>
);

/** A titled horizontal shelf — what the home rows and casino shelves look like. */
export const SkeletonShelf = ({
  cards = 4, cardWidth = 150, imageHeight = 112, className = '',
}: {
  cards?: number;
  cardWidth?: Size;
  imageHeight?: Size;
  className?: string;
}) => (
  <SkeletonRegion label="Loading games" className={`px-2 pt-4 ${className}`}>
    <div className="mb-3 flex items-center justify-between gap-3">
      <Skeleton width={140} height={17} radius={999} />
      <Skeleton width={80} height={12} radius={999} />
    </div>
    <div className="scrollbar-hide -mx-2 flex gap-2 overflow-hidden px-2">
      {Array.from({ length: cards }, (_, i) => (
        <SkeletonCard key={i} width={cardWidth} imageHeight={imageHeight} />
      ))}
    </div>
  </SkeletonRegion>
);

export default Skeleton;
